import React from 'react';
import { useSelector } from 'react-redux';

import { getToday, getYesterday, getWeek, getAllSteps } from '../../../lib/Utils';
import { ToSIPrefix } from '@cognativ/utilities';

import { pfpPlaceholder } from '../../../assets/img';
import {
	MyTeamContainer,
	MyTeamHeader,
	TeamName,
	TeamTotal,
	Table,
	TableHeaders,
	TableHeader,
	TableBody,
	TableRow,
	TableCell,
	ProfilePic,
	ProfileCell,
	Rank,
	ProfileCaptain,
	ProfileName
} from './Styles';
import { ApplicationState } from '../../../lib/Store';
import { PointsType } from '../../../lib/Types';

const MyTeam = () => {
	const {
		participantTeamState: { team },
		statsState: { teamStats },
		participantDashboardState: { teamMembersPoints, dashBoardData }
	} = useSelector((state: ApplicationState) => state);

	const getTeamTotal = () => {
		let total = 0;

		teamStats.slice(0, 5).map(data => {
			const result = getAllSteps(data.steps);
			if (result !== 'none') {
				total += result;
			}
		});

		return ToSIPrefix(total);
	};

	const getSurveyPointsForUser = (userId: string): number => {
		// Get survey points from teamMembersPoints - this is the primary source
		const member = teamMembersPoints?.find(m => m.user.userId === userId);
		if (member && member.surveyPoints !== undefined) {
			return member.surveyPoints;
		}

		// Fallback: for the logged-in user, derive from dashboard rewards
		const currentUserId = dashBoardData?.participantRankView?.userId;
		if (currentUserId && currentUserId === userId) {
			const earnedSurveyRewards = dashBoardData?.rewards
				?.filter(r => r.type === PointsType.Survey && r.earned) || [];
			const totalSurveyPoints = earnedSurveyRewards.reduce((sum, r) => sum + r.points, 0);
			if (totalSurveyPoints > 0) return totalSurveyPoints;
		}

		// Additional fallback: check if survey points exist in dashboard data
		if (dashBoardData?.participantRankView?.userId === userId && dashBoardData?.participantRankView?.surveyPoints) {
			return dashBoardData.participantRankView.surveyPoints;
		}

		return 0;
	};

	return (
		<MyTeamContainer>
			<MyTeamHeader>
				<TeamName>{team?.name === '' || !team?.name ? 'No team name yet!' : team.name}</TeamName>
				<TeamTotal>Team Steps Total: {getTeamTotal()}</TeamTotal>
			</MyTeamHeader>
			<Table>
				<TableHeaders>
					<TableHeader />
					<TableHeader dropable={true}>Today</TableHeader>
					<TableHeader dropable={true}>This Week</TableHeader>
					<TableHeader dropable={true}>Total Steps Tracked</TableHeader>
					<TableHeader dropable={true}>Connection Points</TableHeader>
					<TableHeader dropable={true}>Movement Points</TableHeader>
					<TableHeader dropable={true}>Survey Points</TableHeader>
					<TableHeader>Total Points</TableHeader>
				</TableHeaders>
				<TableBody>
					{teamStats.slice(0, 5).map((data, index) => (
						<TableRow highlight={index % 2 == 0} key={index}>
							<TableCell highlight={index % 2 == 0} dropable={false}>
								<ProfileCell>
									<Rank>{index + 1}</Rank>
									<ProfilePic image={data.profileImage ? data.profileImage : pfpPlaceholder} />
									<ProfileName>{data.name}</ProfileName>
									{data.teamCaptain && <ProfileCaptain>(Captain)</ProfileCaptain>}
								</ProfileCell>
							</TableCell>
							<TableCell
								none={getToday(data.steps) === 'none'}
								highlight={index % 2 == 0}
								dropable={true}
							>
								{getToday(data.steps)}
							</TableCell>
							<TableCell
								none={getWeek(data.steps) === 'none'}
								highlight={index % 2 == 0}
								dropable={true}
							>
								{getWeek(data.steps)}
							</TableCell>
							<TableCell
								none={getAllSteps(data.steps) === 'none'}
								highlight={index % 2 == 0}
								dropable={true}
							>
								{typeof getAllSteps(data.steps) === 'number'
									? ToSIPrefix(getAllSteps(data.steps) as number)
									: getAllSteps(data.steps)}
							</TableCell>
							<TableCell highlight={index % 2 == 0} dropable={true}>
								{data.connectionPoints}
							</TableCell>
							<TableCell highlight={index % 2 == 0} dropable={true}>
								{data.movementPoints}
							</TableCell>
							<TableCell highlight={index % 2 == 0} dropable={true}>
								{getSurveyPointsForUser(data.userId)}
							</TableCell>
							<TableCell highlight={index % 2 == 0} dropable={false}>
								{data.connectionPoints + data.movementPoints + getSurveyPointsForUser(data.userId)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</MyTeamContainer>
	);
};

export default MyTeam;
